import axios from 'axios';
import axiosRetry from 'axios-retry';
import * as Sentry from "@sentry/vue";
import ENVS, { INITIAL_INDEX } from '@/modules/envs';

const { host, key } = ENVS.api[INITIAL_INDEX];
const defaultAxiosConfigs = {
  baseURL: host,
  timeout: 180000,
  headers: { 'X-Requested-With': 'XMLHttpRequest' },
  params: { apikey: key },
};
const retryCondition = (error) => {
  if (error.config.url.includes('feedbacks')) return false;
  const CODE_EXCLUDE_LIST = ['ERR_CANCELED'];
  const isNetworkError = !error.response &&
    Boolean(error.code) && // Prevents retrying cancelled requests
    !CODE_EXCLUDE_LIST.includes(error.code); // Prevents retrying timed out & cancelled requests
  const isRetryableError = error.code !== 'ECONNABORTED' && (!error.response);
  return isNetworkError || isRetryableError;
}
const mtm = axios.create(defaultAxiosConfigs);
const user = axios.create(defaultAxiosConfigs);
axiosRetry(mtm, { retries: 3, retryDelay: axiosRetry.exponentialDelay, shouldResetTimeout: true, retryCondition });
axiosRetry(user, { retries: 3, retryDelay: axiosRetry.exponentialDelay, shouldResetTimeout: true, retryCondition });

const onRequest = config => {
  console.log(`[Request] ${config.method.toUpperCase()} ${config.url} ${config['axios-retry'] != null ? `(retry-${config['axios-retry'].retryCount})` : ''}`);
  return config;
};

const success = res => {
  const { config, status } = res;

  // "axios-retry" repeatedly calls the callback with the same content, causing log noise, so it should be ignored.
  if (config && config['axios-retry'] && config['axios-retry'].retryCount > 0) {
    config['axios-retry'].retryCount -= 1;
    return res;
  }

  console.log(`[Response] ${config.method.toUpperCase()} ${status} ${config.url}`);
  return res;
};

const sanitizeHeaders = (headers) => {
  if (!headers) return {};
  const { 'X-User-Access-Token': token, ...safeHeaders } = headers;
  return safeHeaders;
};

const sanitizeUrl = (url) => {
  if (!url) return '';
  return url.replace(/apikey=[^&]*/, 'apikey=****');
}

const logError = err => {
  const { config, message, response } = err || {};
  const { url: rawUrl, method, data, headers, baseURL, params, timeout } = config || {};
  const { data: resp } = response || {};
  const url = sanitizeUrl(rawUrl);

  // "axios-retry" repeatedly calls the callback with the same content, causing log noise, so it should be ignored.
  if (config && config['axios-retry'] && config['axios-retry'].retryCount > 0) {
    config['axios-retry'].retryCount -= 1;
    return Promise.reject(err);
  }

  if (url.indexOf('/feedbacks') > -1) {
    console.log('Failed to send feedback');
  } else {
    console.log(
      '[Request] [Error]:',
      message,
      resp || 'NO RESPONSE',
      `${method} ${url}`,
      params,
      data || 'NO DATA',
      sanitizeHeaders(headers),
      baseURL,
      timeout
    );

    const sentryTags = {
      source: 'api',
      api_endpoint: url,
      api_method: method?.toUpperCase(),
      api_status: response?.status,
      api_errno: resp?.error?.errno,
    };

    if (url.indexOf('/api/v1/scanners') > -1) {
      try {
        const parsedUrl = url.startsWith('http') ? new URL(url) : new URL(url, baseURL);
        const paths = parsedUrl.pathname.split('/').filter(Boolean);
        const [, , , scannerId, tid, action] = paths;
        sentryTags.scanner_id = scannerId;
        sentryTags.tid = tid;
        sentryTags.api_action = action || "initial";
      }
      catch (e) {
        console.log('[Error]: Failed to parse scannerId and tid', url);
      }
    }

    Sentry.captureMessage(`[Request] [Error]: ${method?.toUpperCase()} ${url} (${sentryTags.api_errno ?? sentryTags.api_status ?? 0})`, {
      level: 'error',
      tags: sentryTags,
    });
  }
  return Promise.reject(err);
};

mtm.interceptors.request.use(onRequest);
mtm.interceptors.response.use(success, logError);
user.interceptors.request.use(onRequest);
user.interceptors.response.use(success, logError);

const $api = {
  mtm,
  user,
};

export default $api;
